import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, RefreshCw, Inbox, FileText, ChevronLeft, Loader2 } from 'lucide-react';
import ReviewWorkflowActions from './ReviewWorkflowActions';
import { getAssessmentReviewQueue } from '../../services/reviewWorkflowApi';
import PageHeader from '../Shared/PageHeader';
import { Avatar, EmptyState } from '../ui';

/**
 * Approver-facing queue of assessments awaiting a review decision (FECOA-1592).
 * Lists everything in the `in_review` state the current user can see, with
 * inline Approve / Deny actions. The backend enforces the approve permission
 * and the approver-!=-submitter guard, so this page is safe to show broadly.
 */
const currentUserId = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u?.id ?? u?.user_id ?? u?.pk ?? null;
  } catch {
    return null;
  }
};

// The auth payload exposes email (not always the pk), so email is the reliable
// identity for "did I submit this?" — mirrors ReviewWorkflowActions.
const currentUserEmail = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}')?.email ?? null;
  } catch {
    return null;
  }
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const ReviewQueuePage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const userId = currentUserId();
  const userEmail = currentUserEmail();

  // Split the queue: items I submitted (Recall only) vs. items awaiting my
  // decision (submitted by others). The same submitter detection drives the
  // per-row actions in ReviewWorkflowActions.
  const isMine = useCallback((item) => {
    const subId =
      item?.submitted_by && typeof item.submitted_by === 'object'
        ? item.submitted_by.id
        : item?.submitted_by;
    const byId = userId != null && subId != null && String(userId) === String(subId);
    const byEmail =
      !!userEmail &&
      !!item?.submitted_by_email &&
      userEmail.toLowerCase() === String(item.submitted_by_email).toLowerCase();
    return byId || byEmail;
  }, [userId, userEmail]);

  const awaitingReview = items.filter((i) => !isMine(i));
  const mySubmissions = items.filter(isMine);

  const renderItem = (item) => (
    <tr key={item.uuid || item.id} className="tr-interactive align-middle">
      <td className="td-cell">
        <div className="flex min-w-0 items-center gap-2.5">
          <FileText className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
          <button
            onClick={() => item.uuid && navigate(`/assessment-builder/${item.uuid}`)}
            className="min-w-0 truncate text-left font-medium text-gray-900 transition-colors hover:text-primary-700 dark:text-gray-50 dark:hover:text-primary-400"
            title="Open assessment"
          >
            {item.name || 'Untitled Assessment'}
          </button>
        </div>
      </td>
      <td className="td-cell">
        <span className="flex min-w-0 items-center gap-2">
          <Avatar name={item.submitted_by_email || ''} size="xs" />
          <span className="truncate">{item.submitted_by_email || 'Unknown submitter'}</span>
        </span>
      </td>
      <td className="td-cell hidden whitespace-nowrap text-[13px] tabular-nums text-gray-500 md:table-cell dark:text-gray-400">
        {formatDate(item.submitted_at)}
      </td>
      <td className="td-cell">
        <div className="flex justify-end">
          <ReviewWorkflowActions
            entity={item}
            entityType="assessment"
            currentUserId={userId}
            isApprover
            onChanged={fetchQueue}
            onReview={(entity) =>
              entity.uuid &&
              navigate(`/assessment-builder/review/${entity.uuid}`, {
                state: { reviewEntity: entity },
              })
            }
            size="md"
          />
        </div>
      </td>
    </tr>
  );

  // Table card shell shared by both queue sections.
  const renderTable = (rows) => (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-y border-gray-100 dark:border-white/[0.06]">
            <th scope="col" className="th-cell">Assessment</th>
            <th scope="col" className="th-cell">Submitted by</th>
            <th scope="col" className="th-cell hidden md:table-cell">Submitted</th>
            <th scope="col" className="th-cell"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-white/[0.06]">{rows.map(renderItem)}</tbody>
      </table>
    </div>
  );

  // silent=true refreshes in the background (no spinner/error reset) for the
  // poll + focus refetches, so the queue stays current without flicker.
  const fetchQueue = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const data = await getAssessmentReviewQueue();
      setItems(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      if (!silent) {
        setError('Could not load the review queue.');
        setItems([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // Keep the queue live: silent refetch every 30s and on tab focus, so newly
  // submitted assessments appear without a manual refresh.
  useEffect(() => {
    const tick = () => fetchQueue(true);
    const interval = setInterval(tick, 30000);
    const onFocus = () => {
      if (document.visibilityState === 'visible') tick();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [fetchQueue]);

  return (
    <div>
      {/* Back to the assessment cards */}
      <button
        onClick={() => navigate('/assessment-builder')}
        className="back-link"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        <span>Back to Assessment Builder</span>
      </button>

      {/* Header */}
      <PageHeader
        icon={ClipboardCheck}
        title="Review Queue"
        subtitle="Assessments submitted for review and awaiting a decision."
      >
        <button
          onClick={fetchQueue}
          disabled={loading}
          className="btn btn-secondary"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
          <span>Refresh</span>
        </button>
      </PageHeader>

      {/* States */}
      {error && (
        <div className="mb-5 rounded-lg border border-danger-100 bg-danger-50 px-4 py-3 text-sm text-danger-700 dark:border-danger-500/30 dark:bg-danger-500/10 dark:text-danger-400">
          {error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="card">
          <EmptyState icon={Inbox} title="Nothing is awaiting review right now." />
        </div>
      )}

      {loading && (
        <div className="card flex items-center justify-center gap-2 py-16 text-[13px] text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Loading review queue…
        </div>
      )}

      {/* Grouped list: items needing my decision first, then my own submissions */}
      {!loading && !error && items.length > 0 && (
        <div className="space-y-5">
          <section className="card overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
              <div className="flex items-center gap-2">
                <h2 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">Awaiting your review</h2>
                <span className="badge badge-gray tabular-nums">{awaitingReview.length}</span>
              </div>
            </div>
            {awaitingReview.length > 0 ? (
              renderTable(awaitingReview)
            ) : (
              <div className="border-t border-gray-100 dark:border-white/[0.06]">
                <EmptyState icon={ClipboardCheck} title="Nothing is waiting on your decision right now." />
              </div>
            )}
          </section>

          {mySubmissions.length > 0 && (
            <section className="card overflow-hidden">
              <div className="px-5 py-4">
                <div className="flex items-center gap-2">
                  <h2 className="text-[15px] font-semibold text-gray-900 dark:text-gray-50">Your submissions</h2>
                  <span className="badge badge-gray tabular-nums">{mySubmissions.length}</span>
                </div>
                <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">
                  Submitted by you and awaiting another approver. Recall to make edits.
                </p>
              </div>
              {renderTable(mySubmissions)}
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewQueuePage;
